import React, { useMemo, useState } from 'react';
import { Transaction } from '../types';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowDownLeft, CheckCircle2 } from 'lucide-react';
interface DebtsPageProps {
  transactions: Transaction[];
  onUpdateTransaction: (t: Transaction) => void;
  onTabChange: (tab: 'borrowed' | 'lent') => void;
}
export function DebtsPage({
  transactions,
  onUpdateTransaction,
  onTabChange
}: DebtsPageProps) {
  const [activeTab, setActiveTab] = useState<'borrowed' | 'lent'>('borrowed');
  const handleTabChange = (tab: 'borrowed' | 'lent') => {
    setActiveTab(tab);
    onTabChange(tab);
  };
  const debts = useMemo(() => {
    return transactions.filter(t => t.isDebt && !t.debtReturned);
  }, [transactions]);
  const borrowed = debts.filter(t => t.type === 'income'); // I borrowed money
  const lentOut = debts.filter(t => t.type === 'expense'); // I lent money
  const currentList = activeTab === 'borrowed' ? borrowed : lentOut;
  const handleMarkReturned = (t: Transaction) => {
    if (confirm(`Mark ${t.debtPerson}'s debt as returned?`)) {
      onUpdateTransaction({
        ...t,
        debtReturned: true
      });
    }
  };
  return <div className="space-y-6 pb-24" data-id="element-339">
      <h1 className="text-2xl font-bold text-stone-50 px-2" data-id="element-340">Debts & Loans</h1>

      {/* Tabs */}
      <div className="flex bg-stone-800 p-1 rounded-2xl" data-id="element-341">
        <button onClick={() => handleTabChange('borrowed')} className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'borrowed' ? 'bg-stone-700 text-red-400 shadow-sm' : 'text-stone-400'}`} data-id="element-342">
          Borrowed
        </button>
        <button onClick={() => handleTabChange('lent')} className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'lent' ? 'bg-stone-700 text-emerald-400 shadow-sm' : 'text-stone-400'}`} data-id="element-343">
          Lent Out
        </button>
      </div>

      {/* List */}
      <div className="space-y-3" data-id="element-344">
        {currentList.length === 0 ? <div className="text-center py-12 text-stone-500" data-id="element-345">
            <p data-id="element-346">No active debts found.</p>
          </div> : currentList.map((t, i) => <motion.div key={t.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.05
      }} className="bg-stone-800 rounded-2xl p-5 border border-stone-700/30" data-id="element-347">
              <div className="flex justify-between items-start mb-4" data-id="element-348">
                <div className="flex items-center gap-3" data-id="element-349">
                  <div className={`p-3 rounded-xl ${activeTab === 'lent' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`} data-id="element-350">
                    {activeTab === 'lent' ? <ArrowUpRight size={20} data-id="element-351" /> : <ArrowDownLeft size={20} data-id="element-352" />}
                  </div>
                  <div data-id="element-353">
                    <h3 className="text-stone-100 font-bold text-lg" data-id="element-354">
                      {t.debtPerson}
                    </h3>
                    <p className="text-stone-500 text-xs" data-id="element-355">
                      {new Date(t.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span className="text-xl font-bold text-stone-50" data-id="element-356">
                  ${t.amount.toLocaleString()}
                </span>
              </div>

              <button onClick={() => handleMarkReturned(t)} className="w-full py-3 bg-stone-900 hover:bg-stone-950 text-stone-300 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors border border-stone-800" data-id="element-357">
                <CheckCircle2 size={16} data-id="element-358" />
                Mark as Returned
              </button>
            </motion.div>)}
      </div>
    </div>;
}